import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import KidStarFilledSvg from 'js/design_system/icons/kid_star_filled.svg';
import { FeedbackPersistence } from 'js/feedback/persistence';
import { CloseButton as CloseButtonBase } from 'js/overlays/CloseButton';
import {
  DashboardOverlay,
  useRegisterOverlay,
} from 'js/overlays/overlayRegistry';

export type FeedbackPhase = 'rating' | 'comment' | 'dismiss';
export type FeedbackTrigger = 'interaction' | 'dwell';

export const DEFAULT_FEEDBACK_CONFIG = {
  fallbackShowDelayMs: 20_000,
  postInteractionDelayMs: 2_000,
  postSuppressionDelayMs: 3_000,
  scrollInteractionThresholdPx: 100,
  thanksAutoCloseMs: 3500,
  commentLimit: 2000,
};

export type FeedbackConfig = typeof DEFAULT_FEEDBACK_CONFIG;

const Popover = styled.aside`
  @import 'stylesheets/design_system/breakpoint';

  position: fixed;
  bottom: var(--spacing-sm-24, 24px);
  right: var(--spacing-sm-24, 24px);
  width: 400px;
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  z-index: 100;

  /* Outranked after already opening: hide rather than unmount, since the show is
     already recorded and re-opening would burn a second one. */
  &[data-suppressed] {
    display: none;
  }

  @include breakpoint(small) {
    bottom: var(--spacing-xs-16, 16px);
    right: var(--spacing-xs-16, 16px);
    left: var(--spacing-xs-16, 16px);
    width: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #141413;
  color: #ffffff;
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
`;

const HeaderTitle = styled.span`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`;

// Reset + focus ring come from the shared CloseButton; this only adds the white
// glyph and hover wash for the dark header band it sits in.
const CloseButton = styled(CloseButtonBase)`
  color: #ffffff;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-xs-16, 16px);

  /* DS Body adds default <p> margin/line-height that distorts the layout when
     used inline as a label; flatten it. */
  & > * {
    margin: 0;
  }
`;

const FieldLabel = styled.label`
  display: inline-block;
`;

const OptionalText = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: var(--text-secondary, #6d6d7d);
  font-weight: 400;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);

  & > * {
    margin: 0;
  }
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  height: 28px;
  width: fit-content;
`;

const StarButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
`;

const StarIconOutlined = styled(Icon)`
  color: var(--text-tertiary, #999);
  transition: color 0.15s ease;
  width: 28px;
  height: 28px;
`;

const StarIconFilled = styled.span`
  color: var(--button-primary-fill, #0645b1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: var(--spacing-xs-12, 12px);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: 8px;
  font: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--text-primary, #141413);
  }
`;

const ThanksBlock = styled.div`
  padding: var(--spacing-md-32, 32px) var(--spacing-sm-24, 24px);
  text-align: center;
  font-size: 16px;
  color: var(--text-primary, #141413);
`;

export interface FeedbackPopoverProps {
  persistence: FeedbackPersistence;
  emit: (
    phase: FeedbackPhase,
    rating: number | null,
    comment: string,
  ) => Promise<unknown> | void;
  onShow: (reason: FeedbackTrigger) => void;
  onDismiss: (hadRating: boolean) => void;
  onSubmit: (rating: number, hasComment: boolean) => void;
  onOpenChange?: (open: boolean) => void;
  userInteracted?: boolean;
  suppressed?: boolean;
  forceShow?: boolean;
  commentId?: string;
  config?: Partial<FeedbackConfig>;
}

export const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({
  persistence,
  emit,
  onShow,
  onDismiss,
  onSubmit,
  onOpenChange,
  userInteracted = false,
  suppressed = false,
  forceShow = false,
  commentId = 'feedback-comment',
  config,
}) => {
  const {
    fallbackShowDelayMs,
    postInteractionDelayMs,
    postSuppressionDelayMs,
    scrollInteractionThresholdPx,
    thanksAutoCloseMs,
    commentLimit,
  } = { ...DEFAULT_FEEDBACK_CONFIG, ...config };
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [serverEligible, setServerEligible] = useState<boolean | null>(null);
  const [triggerReason, setTriggerReason] = useState<FeedbackTrigger | null>(
    null,
  );
  const [hasShown, setHasShown] = useState(false);
  useEffect(() => {
    onOpenChange?.(open);
    return () => onOpenChange?.(false);
  }, [open, onOpenChange]);
  // Delay reopening after a blocking surface closes.
  const [suppressionSettled, setSuppressionSettled] = useState(true);
  const clickthroughRecordedRef = useRef(false);
  const starRowRef = useRef<HTMLDivElement>(null);

  useRegisterOverlay(DashboardOverlay.Feedback, open && !suppressed);

  useEffect(() => {
    const node = starRowRef.current;
    if (!node) return undefined;
    const onLeave = () => setHoverRating(0);
    node.addEventListener('mouseleave', onLeave);
    return () => node.removeEventListener('mouseleave', onLeave);
  }, [open]);

  const markClickthrough = useCallback(
    (extraData: { rating: number; has_comment: boolean }) => {
      if (clickthroughRecordedRef.current) return;
      clickthroughRecordedRef.current = true;
      persistence.recordClickthrough(extraData);
    },
    [persistence],
  );

  // 1. Ask the dismissibles service whether this user is eligible to see the popover.
  //    Resolves once on mount. The wrapper may bypass eligibility for QA.
  useEffect(() => {
    if (forceShow) {
      setServerEligible(true);
      return;
    }
    let active = true;
    persistence.eligible().then(
      (eligible) => {
        if (active) setServerEligible(eligible);
      },
      () => {
        if (active) setServerEligible(false);
      },
    );
    return () => {
      active = false;
    };
  }, [persistence, forceShow]);

  // 2. Treat a meaningful scroll as an interaction signal. The dashboard scrolls
  //    inside <main> on desktop (window stays at 0), so we capture at the document
  //    and look at whichever scrolling element raised the event.
  useEffect(() => {
    if (scrolled) return undefined;
    const onScroll = (e: Event) => {
      const target = e.target;
      const amount =
        target instanceof Element ? target.scrollTop : window.scrollY;
      if (amount > scrollInteractionThresholdPx) setScrolled(true);
    };
    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true });
  }, [scrolled, scrollInteractionThresholdPx]);

  const hasEngaged = userInteracted || scrolled;

  // 3. Hold back while a blocking surface is up, plus a beat after it closes.
  useEffect(() => {
    if (suppressed) {
      setSuppressionSettled(false);
      return undefined;
    }
    if (suppressionSettled) return undefined;
    const timer = setTimeout(
      () => setSuppressionSettled(true),
      postSuppressionDelayMs,
    );
    return () => clearTimeout(timer);
  }, [suppressed, suppressionSettled, postSuppressionDelayMs]);

  // 4. Fire the engagement trigger: 2s after first sort/group/scroll, otherwise
  //    a 20s dwell fallback. Only ever fires once. Paused while outranked — the
  //    tour scrolls the page itself, which would otherwise count as engagement.
  useEffect(() => {
    if (triggerReason || suppressed) return undefined;
    const reason: 'interaction' | 'dwell' = hasEngaged
      ? 'interaction'
      : 'dwell';
    const delay = hasEngaged ? postInteractionDelayMs : fallbackShowDelayMs;
    const timer = setTimeout(() => setTriggerReason(reason), delay);
    return () => clearTimeout(timer);
  }, [
    triggerReason,
    hasEngaged,
    suppressed,
    postInteractionDelayMs,
    fallbackShowDelayMs,
  ]);

  // 5. Open once every gate is clear: server eligible, trigger fired, nothing
  //    outranking us. One-shot: hasShown prevents re-opening (and re-firing
  //    recordShow) after close. Deferring rather than opening-then-hiding is what
  //    stops a covered popover burning one of the user's 3 shows unseen.
  useEffect(() => {
    if (
      hasShown ||
      !serverEligible ||
      !triggerReason ||
      suppressed ||
      !suppressionSettled
    ) {
      return;
    }
    setHasShown(true);
    setOpen(true);
    persistence.recordShow();
    onShow(triggerReason);
  }, [
    hasShown,
    serverEligible,
    triggerReason,
    suppressed,
    suppressionSettled,
    persistence,
    onShow,
  ]);

  const close = useCallback(
    (reason: 'dismiss' | 'submit') => {
      setOpen(false);
      if (reason === 'dismiss') {
        persistence.recordDismissal();
        emit('dismiss', rating ?? null, comment.trim());
        onDismiss(rating != null);
      }
      // 'submit' bookkeeping (recordClickthrough) happens immediately in handleSubmit;
      // by the time we close from the Thanks timer, that's already been logged.
    },
    [rating, comment, emit, persistence, onDismiss],
  );

  const handleSubmit = useCallback(async () => {
    if (rating == null || submitting) return;
    setSubmitting(true);
    markClickthrough({
      rating,
      has_comment: comment.trim().length > 0,
    });
    persistence.recordDismissal();
    try {
      await emit('comment', rating, comment.trim());
      onSubmit(rating, comment.trim().length > 0);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }, [
    rating,
    comment,
    submitting,
    emit,
    markClickthrough,
    persistence,
    onSubmit,
  ]);

  // Dismissal is already recorded when Submit is clicked.
  useEffect(() => {
    if (!submitted) return undefined;
    const timer = setTimeout(() => close('submit'), thanksAutoCloseMs);
    return () => clearTimeout(timer);
  }, [submitted, close, thanksAutoCloseMs]);

  if (!open) return null;

  return (
    <Popover
      role="dialog"
      aria-label="Help us improve"
      data-suppressed={suppressed ? '' : undefined}
    >
      <Header>
        <HeaderTitle>Help us improve</HeaderTitle>
        {!submitted && (
          <CloseButton
            aria-label="Close feedback"
            onClick={() => close('dismiss')}
          />
        )}
      </Header>
      {submitted ? (
        <ThanksBlock>Thanks for your feedback!</ThanksBlock>
      ) : (
        <BodyWrapper>
          <FieldGroup>
            <Body as="span" size="sm" bold>
              Rate your experience
            </Body>
            <StarRow
              ref={starRowRef}
              role="radiogroup"
              aria-label="Rate your experience"
            >
              {[1, 2, 3, 4, 5].map((value) => {
                const displayRating = hoverRating || rating || 0;
                const filled = value <= displayRating;
                return (
                  <StarButton
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={rating === value}
                    aria-label={`${value} star${value === 1 ? '' : 's'}`}
                    onMouseEnter={() => setHoverRating(value)}
                    onClick={() => {
                      setRating(value);
                      setHoverRating(0);
                      markClickthrough({
                        rating: value,
                        has_comment: comment.trim().length > 0,
                      });
                      emit('rating', value, comment.trim());
                    }}
                  >
                    {filled ? (
                      <StarIconFilled>
                        <KidStarFilledSvg width={28} height={28} />
                      </StarIconFilled>
                    ) : (
                      <StarIconOutlined name="kid_star" size={28} />
                    )}
                  </StarButton>
                );
              })}
            </StarRow>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor={commentId}>
              <Body as="span" size="sm" bold>
                Anything else?
              </Body>{' '}
              <OptionalText>(optional)</OptionalText>
            </FieldLabel>
            <CommentTextarea
              id={commentId}
              placeholder="Tell us more about your experience"
              value={comment}
              onChange={(e) =>
                setComment(e.target.value.slice(0, commentLimit))
              }
              rows={3}
            />
          </FieldGroup>

          <Button
            size="sm"
            fullWidth
            iconRight="arrow_forward"
            onClick={handleSubmit}
            disabled={rating == null || submitting}
          >
            {submitting
              ? 'Sending…'
              : comment.trim().length > 0
                ? 'Send feedback'
                : 'Done'}
          </Button>
        </BodyWrapper>
      )}
    </Popover>
  );
};
